import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, primaryWA, waLink } from "@/lib/site";

const initial = {
  nama: "",
  perusahaan: "",
  whatsapp: "",
  kota: "",
  jenis_produk: "",
  ketebalan: "",
  warna: "",
  estimasi_jumlah: "",
  detail: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initial);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWaMessage = (f) => `Halo ANIMAX Acrylic, saya ingin meminta penawaran:
Nama: ${f.nama}
Perusahaan: ${f.perusahaan || "-"}
WhatsApp: ${f.whatsapp}
Kota: ${f.kota || "-"}
Jenis Produk: ${f.jenis_produk || "-"}
Ketebalan: ${f.ketebalan || "-"}
Warna: ${f.warna || "-"}
Estimasi Jumlah: ${f.estimasi_jumlah || "-"}
Pesan / Detail Kebutuhan: ${f.detail || "-"}`;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nama || !form.whatsapp) {
      toast.error("Mohon lengkapi Nama dan Nomor WhatsApp");
      return;
    }
    window.open(waLink(primaryWA.raw, buildWaMessage(form)), "_blank");
  };

  return (
    <section id="kontak" data-testid="contact-section" className="py-24 lg:py-32 bg-animax-gray">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-16">
          <div className="tag-uppercase text-animax-orange mb-4">Kontak & Penawaran</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-animax-black">
            Hubungi PT. Animax Putra Lancar
          </h2>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            Kami siap membantu kebutuhan proyek acrylic Anda. Hubungi kami melalui WhatsApp, telepon,
            email, atau kirim permintaan penawaran melalui form di bawah.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-wa">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-animax-yellow flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-animax-black" />
                </div>
                <div className="flex-1">
                  <div className="tag-uppercase text-neutral-500 mb-2">WhatsApp</div>
                  <div className="space-y-2">
                    {COMPANY.whatsapp.map((w, i) => (
                      <div key={i} className="flex items-center justify-between gap-2" data-testid={`contact-wa-row-${i}`}>
                        <a
                          href={waLink(w.raw, `Halo ${w.name}, saya ingin bertanya tentang ANIMAX Acrylic`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`contact-wa-${i}`}
                          className="flex-1 flex items-center justify-between group hover:text-animax-orange transition-colors"
                        >
                          <span className="text-animax-black text-sm font-medium">{w.number}</span>
                          <span className="tag-uppercase text-neutral-400 group-hover:text-animax-orange">{w.name}</span>
                        </a>
                        <a
                          href={`tel:${w.tel}`}
                          data-testid={`contact-tel-${i}`}
                          className="p-1.5 rounded-full bg-neutral-100 hover:bg-animax-yellow transition-colors"
                          aria-label={`Telepon ${w.name}`}
                          title={`Telepon ${w.name}`}
                        >
                          <Phone className="w-3.5 h-3.5 text-animax-black" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-email-card">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-animax-black" />
                </div>
                <div>
                  <div className="tag-uppercase text-neutral-500 mb-2">Email</div>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="text-animax-black text-sm font-medium hover:text-animax-orange break-all">
                    {COMPANY.email}
                  </a>
                  <a
                    href={COMPANY.instagram.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    className="mt-3 flex items-center gap-2 text-animax-black text-sm font-medium hover:text-animax-orange transition-colors"
                  >
                    <Instagram className="w-4 h-4 shrink-0" />
                    {COMPANY.instagram.label}
                  </a>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-address">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-animax-black" />
                </div>
                <div>
                  <div className="tag-uppercase text-neutral-500 mb-2">Alamat</div>
                  <p className="text-animax-black text-sm leading-relaxed">{COMPANY.address}</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200" data-testid="contact-hours">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-animax-black" />
                </div>
                <div className="flex-1">
                  <div className="tag-uppercase text-neutral-500 mb-3">Jam Operasional</div>
                  <div className="space-y-1.5">
                    {COMPANY.hours.map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-neutral-600">{h.day}</span>
                        <span className={`font-medium ${h.time === "Tutup" ? "text-red-500" : "text-animax-black"}`}>{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-neutral-200 aspect-[16/10]" data-testid="contact-map">
              <iframe
                title="Lokasi PT Animax Putra Lancar"
                src={`https://www.google.com/maps?q=${COMPANY.mapsQuery}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="p-8 lg:p-10 rounded-3xl bg-white border border-neutral-200 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
            >
              <h3 className="font-display text-2xl font-medium text-animax-black">Minta Penawaran Harga</h3>
              <p className="mt-2 mb-8 text-sm text-neutral-600 leading-relaxed">
                Isi form berikut, lalu kirim langsung ke WhatsApp tim sales kami.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Nama *">
                  <input value={form.nama} onChange={(e) => setField("nama", e.target.value)} required
                    className={inputCls} placeholder="Nama lengkap" data-testid="quote-nama-input" />
                </Field>
                <Field label="Nama Perusahaan">
                  <input value={form.perusahaan} onChange={(e) => setField("perusahaan", e.target.value)}
                    className={inputCls} placeholder="Opsional" data-testid="quote-perusahaan-input" />
                </Field>
                <Field label="Nomor WhatsApp *">
                  <input value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} required
                    className={inputCls} placeholder="+62 812-xxxx-xxxx" data-testid="quote-wa-input" />
                </Field>
                <Field label="Kota">
                  <input value={form.kota} onChange={(e) => setField("kota", e.target.value)}
                    className={inputCls} placeholder="Kota / Kabupaten" data-testid="quote-kota-input" />
                </Field>
                <Field label="Jenis Produk">
                  <select value={form.jenis_produk} onChange={(e) => setField("jenis_produk", e.target.value)}
                    className={inputCls} data-testid="quote-produk-input">
                    <option value="">Pilih produk</option>
                    <option>Acrylic Sheet Bening</option>
                    <option>Acrylic Sheet Putih</option>
                    <option>Acrylic Sheet Hitam</option>
                    <option>Acrylic Sheet Merah</option>
                    <option>Acrylic Sheet Kuning</option>
                    <option>Acrylic Sheet Biru</option>
                    <option>Lainnya</option>
                  </select>
                </Field>
                <Field label="Ketebalan">
                  <select value={form.ketebalan} onChange={(e) => setField("ketebalan", e.target.value)}
                    className={inputCls} data-testid="quote-tebal-input">
                    <option value="">Pilih ketebalan</option>
                    <option>2mm</option>
                    <option>3mm</option>
                    <option>5mm</option>
                  </select>
                </Field>
                <Field label="Warna">
                  <input value={form.warna} onChange={(e) => setField("warna", e.target.value)}
                    className={inputCls} placeholder="mis. Bening, Putih, Merah" data-testid="quote-warna-input" />
                </Field>
                <Field label="Estimasi Jumlah">
                  <input value={form.estimasi_jumlah} onChange={(e) => setField("estimasi_jumlah", e.target.value)}
                    className={inputCls} placeholder="mis. 10 lembar, 50 lembar" data-testid="quote-qty-input" />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Pesan / Detail Kebutuhan">
                  <textarea rows={4} value={form.detail} onChange={(e) => setField("detail", e.target.value)}
                    className={inputCls + " resize-none"}
                    placeholder="Ceritakan kebutuhan Anda: aplikasi, timeline, lokasi pengiriman, atau pertanyaan lainnya."
                    data-testid="quote-detail-input" />
                </Field>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  data-testid="quote-send-wa"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-animax-orange text-white font-medium text-sm hover:bg-animax-yellow hover:text-animax-black transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  Kirim via WhatsApp
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls = "w-full px-4 py-3 rounded-lg border border-neutral-200 bg-white text-sm text-animax-black placeholder:text-neutral-400 focus:outline-none focus:border-animax-orange focus:ring-2 focus:ring-animax-orange/20 transition-all";

const Field = ({ label, children }) => (
  <div>
    <label className="block tag-uppercase text-neutral-500 mb-2">{label}</label>
    {children}
  </div>
);
